import { useCallback, useEffect, useRef, useState } from "react";
import type { GitHubToolConfirmationRequest } from "../../session-logic";
import {
  isExpiredGitHubConfirmationApprovalError,
  isGitHubReauthRequiredError,
} from "../ChatView.logic";
import { cn } from "~/lib/utils";

type ApprovalOption = 1 | 2 | 3;
type OverlayStatus = "idle" | "submitting" | "done" | "failed" | "reauthRequired";

interface ComposerGitHubApprovalOverlayProps {
  confirmation: GitHubToolConfirmationRequest;
  onApprove: (confirmation: GitHubToolConfirmationRequest) => Promise<void>;
  onDecline?: (confirmation: GitHubToolConfirmationRequest) => Promise<void>;
  onSendFeedback: (text: string) => void;
  onDismiss: () => void;
}

function formatOperationVerb(operation: string): string {
  switch (operation) {
    case "push_branch":
      return "push this branch to GitHub";
    case "create_pull_request":
      return "open a pull request on GitHub";
    case "create_repository":
      return "create a GitHub repository";
    case "refresh_credentials":
      return "refresh GitHub credentials";
    case "rerun_workflow":
      return "rerun a GitHub Actions workflow";
    case "stash":
      return "stash uncommitted changes";
    case "commit":
      return "commit changes to the repository";
    case "reset_hard":
      return "hard reset the repository";
    default:
      return operation
        .replace(/^link_project_repository$/, "link this project to a GitHub repository")
        .replace(/_/g, " ");
  }
}

function buildDetailLines(
  request: GitHubToolConfirmationRequest["request"],
): Array<{ label: string; value: string }> {
  const lines: Array<{ label: string; value: string | null | undefined }> = [
    {
      label: "repo",
      value: (request.repoFullName as string | undefined) ?? formatOwnerRepo(request),
    },
    { label: "branch", value: request.branch as string | undefined },
    { label: "base", value: request.baseBranch as string | undefined },
    { label: "pr", value: request.title as string | undefined },
    { label: "path", value: request.cwd as string | undefined },
  ];
  return lines.filter((l): l is { label: string; value: string } => Boolean(l.value));
}

function formatOwnerRepo(request: Record<string, unknown>): string | undefined {
  const owner = request.owner as string | undefined;
  const repo = request.repo as string | undefined;
  return owner && repo ? `${owner}/${repo}` : undefined;
}

export function ComposerGitHubApprovalOverlay({
  confirmation,
  onApprove,
  onDecline,
  onSendFeedback,
  onDismiss,
}: ComposerGitHubApprovalOverlayProps) {
  const [selected, setSelected] = useState<ApprovalOption>(1);
  const [feedbackText, setFeedbackText] = useState("");
  const [status, setStatus] = useState<OverlayStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [reauthHref, setReauthHref] = useState<string | null>(null);
  const feedbackRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs to give the keyboard handler access to current values without re-attaching.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const detailLines = buildDetailLines(confirmation.request);
  const verb = formatOperationVerb(confirmation.operation);
  const isBusy = status === "submitting";

  const handleSubmit = useCallback(async () => {
    if (isBusy || status === "done") return;
    if (selected === 1) {
      setStatus("submitting");
      setErrorMsg(null);
      try {
        await onApprove(confirmation);
        setStatus("done");
        // Dismiss so parent's isGitHubApprovalOverlayVisible goes false immediately,
        // preventing the overlay from remounting on the same confirmationId.
        onDismiss();
      } catch (err) {
        // CMMD session expired: surface the actionable "Sign in to CMMD" CTA
        // instead of a raw failure string (this overlay is a co-equal approval
        // surface to the inline timeline card).
        if (isGitHubReauthRequiredError(err)) {
          setReauthHref(err.reauthHref);
          setStatus("reauthRequired");
          return;
        }
        // Session-orphan: confirmation is permanently invalid — dismiss silently rather than loop.
        // Use the structured error check (expired flag) rather than substring-matching
        // human-readable error strings, which are brittle across server versions.
        if (isExpiredGitHubConfirmationApprovalError(err)) {
          onDismiss();
          return;
        }
        const msg = err instanceof Error ? err.message : "Approval failed.";
        setStatus("failed");
        setErrorMsg(msg);
      }
    } else if (selected === 2) {
      if (onDecline) {
        await onDecline(confirmation);
        onDismiss();
      } else {
        onDismiss();
      }
    } else {
      if (!feedbackText.trim()) {
        feedbackRef.current?.focus();
        return;
      }
      onSendFeedback(feedbackText.trim());
      onDismiss();
    }
  }, [
    isBusy,
    status,
    selected,
    feedbackText,
    onApprove,
    onDecline,
    confirmation,
    onSendFeedback,
    onDismiss,
  ]);

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  // Claim focus on mount so keyboard shortcuts work immediately.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Focus textarea when option 3 is selected.
  useEffect(() => {
    if (selected === 3) {
      feedbackRef.current?.focus();
    }
  }, [selected]);

  // Keyboard handler — attached once, reads current values via refs.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      // Let textarea handle its own keydown (stopPropagation bubbles up here only if not stopped).
      const sel = selectedRef.current;
      if (e.key === "1") {
        e.preventDefault();
        setSelected(1);
      } else if (e.key === "2") {
        e.preventDefault();
        setSelected(2);
      } else if (e.key === "3") {
        e.preventDefault();
        setSelected(3);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(1, s - 1) as ApprovalOption);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(3, s + 1) as ApprovalOption);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onDismissRef.current();
      } else if (e.key === "Enter" && sel !== 3) {
        e.preventDefault();
        void handleSubmitRef.current();
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, []); // empty: attach once on mount, clean up on unmount

  if (status === "done") return null;

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="outline-none"
      role="dialog"
      aria-label="GitHub action approval"
    >
      <div className="overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl ring-1 ring-black/10 dark:ring-white/[0.06]">
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] leading-snug font-medium text-foreground/90">
            Forge needs your approval to{" "}
            <span className="font-semibold text-foreground">{verb}</span>.
          </p>
          {detailLines.length > 0 ? (
            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 rounded-md border border-border bg-muted/35 px-3 py-2">
              {detailLines.map(({ label, value }) => (
                <span key={label} className="flex min-w-0 items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
                    {label}
                  </span>
                  <span className="max-w-[200px] truncate font-mono text-[11px] text-foreground/70">
                    {value}
                  </span>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Options */}
        <div className="border-t border-border py-1">
          <Option
            number={1}
            selected={selected === 1}
            disabled={isBusy}
            onClick={() => setSelected(1)}
            onDoubleClick={() => {
              setSelected(1);
              void handleSubmit();
            }}
          >
            Approve
          </Option>

          <Option
            number={2}
            selected={selected === 2}
            disabled={isBusy}
            onClick={() => setSelected(2)}
            onDoubleClick={() => {
              setSelected(2);
              void handleSubmit();
            }}
          >
            Decline
          </Option>

          <Option
            number={3}
            selected={selected === 3}
            disabled={isBusy}
            onClick={() => setSelected(3)}
            expandable
          >
            <span className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">✎</span>
              No, give the agent different instructions
            </span>
          </Option>

          {/* Textarea is a sibling of the button, not a child — interactive elements cannot nest */}
          {selected === 3 ? (
            <div className="px-4 pb-4">
              <textarea
                ref={feedbackRef}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSubmit();
                  }
                  e.stopPropagation();
                }}
                placeholder="Tell the agent what to do instead…"
                rows={2}
                className="w-full resize-none rounded-md border border-input bg-background/70 px-2.5 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-ring focus:ring-0"
              />
            </div>
          ) : null}
        </div>

        {/* Error */}
        {status === "failed" && errorMsg ? (
          <p role="alert" className="px-4 pb-2 text-[11px] text-destructive-foreground">
            {errorMsg}
          </p>
        ) : null}

        {/* Re-auth required */}
        {status === "reauthRequired" ? (
          <div className="border-t border-border px-4 py-3">
            <p className="text-[11px] leading-4 text-warning-foreground">
              Your CMMD session expired, so Forge can&apos;t run this GitHub action. Sign in to
              CMMD, then reconnect this cloud project and retry.
            </p>
            {reauthHref ? (
              <a
                href={reauthHref}
                className="mt-2 inline-flex items-center rounded-full bg-primary px-4 py-1.5 text-[12px] font-medium text-primary-foreground transition-all hover:bg-primary/90 active:scale-95"
              >
                Sign in to CMMD
              </a>
            ) : null}
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2.5">
          <button
            type="button"
            onClick={onDismiss}
            disabled={isBusy || status === "reauthRequired"}
            className="rounded-md px-3 py-1 text-[12px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
            title={status === "reauthRequired" ? "Sign in to CMMD before dismissing" : undefined}
          >
            Skip
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isBusy || (selected === 3 && !feedbackText.trim())}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-medium transition-all",
              "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95",
              "disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100",
            )}
          >
            {isBusy ? (
              <span className="flex items-center gap-1.5">
                <span
                  aria-label="Running"
                  className="size-3 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground"
                />
                Running…
              </span>
            ) : (
              <>
                Submit
                <kbd className="rounded border border-primary-foreground/20 bg-primary-foreground/15 px-1 py-0.5 text-[10px] leading-none">
                  ↵
                </kbd>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

interface OptionProps {
  number: ApprovalOption;
  selected: boolean;
  disabled?: boolean;
  expandable?: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  children: React.ReactNode;
}

function Option({
  number,
  selected,
  disabled,
  expandable,
  onClick,
  onDoubleClick,
  children,
}: OptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-2.5 text-left text-[13px] transition-colors",
        "disabled:opacity-50",
        selected
          ? "bg-accent text-accent-foreground"
          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        expandable && selected ? "pb-2" : null,
      )}
    >
      <span
        className={cn(
          "mt-[1px] flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold leading-none transition-colors",
          selected
            ? "bg-primary text-primary-foreground"
            : "border border-border bg-muted text-muted-foreground",
        )}
      >
        {number}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
      {selected && !expandable ? (
        <span aria-hidden className="mt-[1px] shrink-0 text-[11px] text-muted-foreground">
          ↑↓
        </span>
      ) : null}
    </button>
  );
}
